import type { CpuPoint, MemPoint, GpuSnapshot, GpuDeviceView, OllamaSnapshot, LoadedModelView } from '../state/metrics';
import { spilloverPct, keepAliveCountdown } from './ollama-derive';

export type Sev = 'crit' | 'warn' | 'good';

export interface Finding {
  id: string;
  sev: Sev;
  title: string;
  detail: string;
  action?: string;
}

/** Free VRAM below this many MB: not enough headroom left to load another model without it spilling onto the CPU. */
const VRAM_FREE_WARN_MB = 400;
/** Free disk below this many GB: a model download could fail partway through. */
const DISK_FREE_WARN_GB = 20;
/** Keep-alive time remaining under this many seconds: warn the model is about to unload. */
const UNLOAD_SOON_WARN_S = 30;

const SEV_ORDER: Record<Sev, number> = { crit: 0, warn: 1, good: 2 };

function round(n: number): number {
  return Math.round(n);
}

function toGb(mb: number, decimals = 0): number {
  const factor = 10 ** decimals;
  return Math.round((mb / 1024) * factor) / factor;
}

/** The device findings talk about: the first discrete (non-shared-VRAM) GPU, else the first device. */
function pickDevice(gpu: GpuSnapshot | null): GpuDeviceView | null {
  if (!gpu || gpu.devices.length === 0) return null;
  return gpu.devices.find((d) => !d.vramShared) ?? gpu.devices[0] ?? null;
}

/** Loaded model with the largest CPU spillover; null if every loaded model fits fully in VRAM. */
function pickWorstSpillover(loaded: LoadedModelView[]): { model: LoadedModelView; pct: number } | null {
  let best: { model: LoadedModelView; pct: number } | null = null;
  for (const m of loaded) {
    const pct = spilloverPct(m.vramMb, m.sizeMb);
    if (pct > 0 && (!best || pct > best.pct)) best = { model: m, pct };
  }
  return best;
}

/**
 * Seconds remaining until keep-alive expiry; null if unknown or already past. Mirrors
 * keepAliveCountdown's own parsing/gate so the two never disagree on "is it still counting down".
 */
function remainingSeconds(expiresAt: string | null, nowMs: number): number | null {
  if (!expiresAt) return null;
  const t = Date.parse(expiresAt);
  if (Number.isNaN(t)) return null;
  const remainingS = Math.floor((t - nowMs) / 1000);
  return remainingS > 0 ? remainingS : null;
}

export function deriveFindings(
  input: { cpu?: CpuPoint; mem?: MemPoint; gpu: GpuSnapshot | null; ollama: OllamaSnapshot | null },
  nowMs: number,
): Finding[] {
  const { mem, gpu, ollama } = input;
  const findings: Finding[] = [];
  const device = pickDevice(gpu);
  const loaded = ollama?.loaded ?? [];

  // Ollama unreachable — nothing model-related can happen until it's back.
  if (ollama && !ollama.reachable) {
    findings.push({
      id: 'ollama-unreachable',
      sev: 'warn',
      title: "Ollama isn't responding",
      detail: "models can't be loaded or unloaded until it comes back — check that the Ollama service is running.",
    });
  }

  // Spillover — the worst-offloaded loaded model, if any spills onto the CPU at all.
  const worst = pickWorstSpillover(loaded);
  if (worst) {
    const vramNote = device?.vramTotalMb != null ? `the card's ${toGb(device.vramTotalMb, 1)} GB` : "the card's memory";
    findings.push({
      id: 'spillover',
      sev: 'crit',
      title: 'Your model is too big for the graphics card',
      detail: `about ${worst.pct}% of ${worst.model.name} runs on the slower CPU because it doesn't fit in ${vramNote} — that's why replies come slowly.`,
      action: '→ Switch to a smaller model like llama3.2:1b — it fits fully on the GPU and runs much faster',
    });
  }

  // Throttling — the card is deliberately capping its own speed.
  if (device && device.throttleReasons.length > 0) {
    const reason = device.throttleReasons[0]!;
    const clockNote = device.clockCoreMhz != null ? ` to ${device.clockCoreMhz} MHz` : '';
    const tempNote = device.tempC != null ? `${device.tempC}°C` : 'the temperature';
    let detail: string;
    if (reason === 'power_cap') {
      const powerNote = device.powerLimitW != null ? `${device.powerLimitW}W` : 'its';
      detail = `it hit its ${powerNote} power limit (normal on a laptop) and dropped its speed${clockNote}. It's not overheating — ${tempNote} is fine — it just can't pull more power.`;
    } else if (reason === 'thermal') {
      detail = `it hit ${tempNote}, which is too hot, and dropped its speed${clockNote} to cool down.`;
    } else {
      detail = `it's throttling (${reason}) and running below full speed${clockNote}.`;
    }
    findings.push({
      id: 'throttle',
      sev: 'warn',
      title: 'The graphics card is running at reduced speed',
      detail,
    });
  }

  // Low free VRAM — only meaningful for a discrete card with known totals.
  if (device && !device.vramShared && device.vramTotalMb != null && device.vramUsedMb != null) {
    const freeMb = device.vramTotalMb - device.vramUsedMb;
    if (freeMb < VRAM_FREE_WARN_MB) {
      findings.push({
        id: 'vram-low',
        sev: 'warn',
        title: 'Almost no spare video memory',
        detail: `only ${round(freeMb)} MB free — you can't load another model without it spilling onto the CPU.`,
      });
    }
  }

  // Swapping / memory pressure. Prefer the active-rate signal (swapOutPerS > 0) since that means
  // the system is swapping right now; but a nonzero swapUsedMb with no rate data still means
  // something is parked on disk, so we treat that as swapping too (crit either way — both mean
  // the box is memory-constrained enough to be using disk as RAM).
  const swapOutActive = mem?.swapOutPerS != null && mem.swapOutPerS > 0;
  const swapUsedMb = mem?.swapUsedMb ?? 0;
  const isSwapping = swapOutActive || swapUsedMb > 0;
  if (mem && isSwapping) {
    findings.push({
      id: 'swapping',
      sev: 'crit',
      title: 'The machine is running out of memory',
      detail: `it's parking ${round(swapUsedMb)} MB on the slow disk — expect lag everywhere until something frees up.`,
    });
  } else if (mem) {
    const ramPct = round((mem.usedMb / mem.totalMb) * 100);
    findings.push({
      id: 'mem-ok',
      sev: 'good',
      title: 'Memory is fine',
      detail: `only ${ramPct}% of RAM is used and the system isn't dumping memory onto the slow disk — nothing is bottlenecked by memory.`,
    });
  }

  // Disk space for model downloads.
  if (mem && mem.diskFreeMb != null) {
    const freeGb = toGb(mem.diskFreeMb);
    if (freeGb < DISK_FREE_WARN_GB) {
      findings.push({
        id: 'disk-low',
        sev: 'warn',
        title: 'Low on storage for models',
        detail: `only ${freeGb} GB left — downloading a new model may fail partway.`,
      });
    } else {
      const totalGb = mem.diskTotalMb != null ? toGb(mem.diskTotalMb) : null;
      findings.push({
        id: 'disk-ok',
        sev: 'good',
        title: 'Plenty of room for models',
        detail:
          totalGb != null
            ? `${freeGb} GB free of ${totalGb} GB — enough for several more downloads.`
            : `${freeGb} GB free — enough for several more downloads.`,
      });
    }
  }

  // A loaded model is about to unload — the next request will pay the cold-load cost again.
  for (const m of loaded) {
    const remaining = remainingSeconds(m.expiresAt, nowMs);
    if (remaining != null && remaining < UNLOAD_SOON_WARN_S) {
      const countdown = keepAliveCountdown(m.expiresAt, nowMs) ?? `${remaining}s`;
      findings.push({
        id: 'unload-soon',
        sev: 'good',
        title: `${m.name} unloads in ${countdown}`,
        detail: 'the next request will trigger a slow cold reload.',
      });
      break; // one call-to-attention is enough; avoid piling up duplicate ids for multiple models
    }
  }

  return findings.sort((a, b) => SEV_ORDER[a.sev] - SEV_ORDER[b.sev]);
}

export function overallStatus(findings: Finding[]): { level: Sev; label: string } {
  const crit = findings.filter((f) => f.sev === 'crit').length;
  const warn = findings.filter((f) => f.sev === 'warn').length;
  if (crit === 0 && warn === 0) {
    return { level: 'good', label: 'Everything looks healthy' };
  }
  const parts = [crit > 0 ? `${crit} critical` : null, warn > 0 ? `${warn} warning` : null].filter(
    (p): p is string => p != null,
  );
  return { level: crit > 0 ? 'crit' : 'warn', label: `${parts.join(' · ')} — needs attention` };
}
